import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { lightTheme, darkTheme } from './theme';

import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProcessamentoPage from './pages/ProcessamentoPage';
import PlanoContasPage from './pages/PlanoContasPage';
import CentrosDeCusto from './pages/CentrosDeCusto';
import AlocacaoCustos from './pages/AlocacaoCustos';
import RateioPorItem from './pages/RateioPorItem';
import RelatorioCustoUnitario from './pages/RelatorioCustoUnitario';
import RelatorioConferencia from './pages/RelatorioConferencia';
import RelatorioAnomalias from './pages/RelatorioAnomalias';
import UnidadesProducao from './pages/UnidadesProducao';
import SimuladorPage from './pages/SimuladorPage';
import TabelasAuxiliaresPage from './pages/TabelasAuxiliaresPage';
import TipoAlocacaoPage from './pages/TipoAlocacaoPage';
import CompetenceManagementPage from './pages/CompetenceManagementPage';
import BudgetManagementPage from './pages/BudgetManagementPage';
import AnaliseOrcamentariaPage from './pages/AnaliseOrcamentariaPage';
import AdvancedCostAnalysisPage from './pages/AdvancedCostAnalysisPage';
import Layout from './components/Layout';
import RelatorioMensal from './pages/RelatorioMensal';
import AdminPage from './pages/AdminPage';
import CompaniesPage from './pages/CompaniesPage';
import AIPage from './pages/AIPage';
import SupplyTablesPage from './pages/SupplyTablesPage';
import ProductionPage from './pages/ProductionPage';
import ProductionAnalysisPage from './pages/ProductionAnalysisPage';
import DemonstrativosPage from './pages/DemonstrativosPage';
import MonitoringPage from './pages/MonitoringPage';
import ServiceLineSetupPage from './pages/ServiceLineSetupPage';
import ProfitabilityPage from './pages/ProfitabilityPage';
import PivotTablePage from './pages/PivotTablePage';
import CostTablesViewPage from './pages/CostTablesViewPage';
import CustoPadraoPage from './pages/CustoPadraoPage';
import DocumentationPage from './pages/DocumentationPage';
import FloatingAssistant from './components/FloatingAssistant';
import TutorialButton from './components/GuidedTutorial';
import TroubleshootingAlert from './components/TroubleshootingAlert';
import OnboardingTour from './components/OnboardingTour';
import FloatingIconsManager from './components/FloatingIconsManager';
import { CompetenceProvider } from './contexts/CompetenceContext';
import './styles/ux-enhancements.css';

import './App.css';


const PrivateRoute = ({ children }) => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    return isAuthenticated ? children : <Navigate to="/" />;
};

const AdminRoute = ({ children }) => {
    const role = (localStorage.getItem('role') || '').trim().toLowerCase();
    console.log('AdminRoute check:', { role });
    if (role !== 'admin') {
        console.warn('AdminRoute: Access denied. Redirecting to dashboard.');
        alert(`DEBUG: Acesso negado. Seu papel no navegador é: "${role}". O sistema espera: "admin"`);
    }
    return role === 'admin' ? children : <Navigate to="/dashboard" />;
};

// Global Error Boundary
// Global Error Boundary
class GlobalErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error("Global Error:", error, errorInfo);
        this.setState({ errorInfo });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: 20, color: 'red', overflow: 'auto', maxHeight: '100vh' }}>
                    <h1>Erro Crítico na Aplicação</h1>
                    <pre>{this.state.error && this.state.error.toString()}</pre>
                    <details style={{ whiteSpace: 'pre-wrap' }}>
                        {this.state.errorInfo && this.state.errorInfo.componentStack}
                    </details>
                </div>
            );
        }
        return this.props.children;
    }
}

const App = () => {
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const savedMode = localStorage.getItem('sach-theme');
        return savedMode === 'dark';
    });

    useEffect(() => {
        localStorage.setItem('sach-theme', isDarkMode ? 'dark' : 'light');
        if (isDarkMode) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, [isDarkMode]);

    const toggleTheme = () => {
        setIsDarkMode(!isDarkMode);
    };

    return (
        <GlobalErrorBoundary>
            <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
                <CssBaseline />
                <Router>
                    <Routes>
                        <Route path="/" element={<LoginPage />} />
                        <Route
                            path="/*"
                            element={
                                <PrivateRoute>
                                    <CompetenceProvider>
                                        <Layout isDarkMode={isDarkMode} toggleTheme={toggleTheme}>
                                            <Routes>
                                                {/* ROTAS PRINCIPAIS */}
                                                <Route path="/dashboard" element={<DashboardPage />} />
                                                <Route path="/processamento" element={<ProcessamentoPage />} />
                                                <Route path="/processamento" element={<ProcessamentoPage />} />
                                                <Route path="/competencias" element={<CompetenceManagementPage />} />

                                                {/* ROTAS DE CONFIGURAÇÃO */}
                                                <Route path="/config" element={<PlanoContasPage />} />
                                                <Route path="/centros-de-custo" element={<CentrosDeCusto />} />
                                                <Route path="/linhas-servico" element={<ServiceLineSetupPage />} />
                                                <Route path="/unidades-producao" element={<UnidadesProducao />} />
                                                <Route path="/tabelas-auxiliares" element={<TabelasAuxiliaresPage />} />
                                                <Route path="/tipos-alocacao" element={<TipoAlocacaoPage />} />
                                                <Route path="/alocacao-custos" element={<AlocacaoCustos />} />
                                                <Route path="/rateio-por-item" element={<RateioPorItem />} />
                                                <Route path="/insumos" element={<SupplyTablesPage />} />
                                                <Route path="/custo-padrao" element={<CustoPadraoPage />} />
                                                <Route path="/producao" element={<ProductionPage />} />
                                                <Route path="/analise-producao" element={<ProductionAnalysisPage />} />

                                                <Route path="/analise-producao" element={<ProductionAnalysisPage />} />
                                                <Route path="/lucratividade" element={<ProfitabilityPage />} />
                                                <Route path="/orcamento" element={<BudgetManagementPage />} />
                                                <Route path="/analise-orcamentaria" element={<AnaliseOrcamentariaPage />} />
                                                <Route path="/analise-custos-avancada" element={<AdvancedCostAnalysisPage />} />

                                                {/* ROTAS DE RELATÓRIO E AUDITORIA */}
                                                <Route path="/relatorio-conferencia" element={<RelatorioConferencia />} />
                                                <Route path="/relatorio-custo-unitario" element={<RelatorioCustoUnitario />} />
                                                <Route path="relatorio-mensal/:mes" element={<RelatorioMensal />} />
                                                <Route path="/relatorio-anomalias" element={<RelatorioAnomalias />} />
                                                <Route path="/simulador" element={<SimuladorPage />} />
                                                <Route path="/ai-analysis" element={<AIPage />} />
                                                <Route path="/demonstrativos" element={<DemonstrativosPage />} />
                                                <Route path="/monitoramento" element={<MonitoringPage />} />
                                                <Route path="/tabela-dinamica" element={<PivotTablePage />} />
                                                <Route path="/cost-tables/:mes" element={<CostTablesViewPage />} />

                                                {/* ROTA ADMIN */}
                                                <Route path="/admin" element={
                                                    <AdminRoute>
                                                        <AdminPage />
                                                    </AdminRoute>
                                                } />
                                                <Route path="/companies" element={
                                                    <AdminRoute>
                                                        <CompaniesPage />
                                                    </AdminRoute>
                                                } />
                                                <Route path="/documentacao" element={
                                                    <AdminRoute>
                                                        <DocumentationPage />
                                                    </AdminRoute>
                                                } />
                                            </Routes>
                                        </Layout>
                                    </CompetenceProvider>
                                    {/* Helper components - only show when authenticated */}
                                    <FloatingIconsManager>
                                        <FloatingAssistant />
                                        <TutorialButton />
                                    </FloatingIconsManager>
                                    <TroubleshootingAlert />
                                    <OnboardingTour />
                                </PrivateRoute>
                            }
                        />
                    </Routes>
                </Router>
            </ThemeProvider>
        </GlobalErrorBoundary>
    );
};

export default App;
